import { useCallback, useEffect, useState } from 'react'

import { CheckIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react'

import { TranscriptionError } from '@pkg/types/interview'

import { NOTETAKER_EMAIL } from '@/consts'
import {
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { PencilSvg } from '@/views/assets/PencilSvg'
import Card from '@/views/components/Card'
import { CopyContentButton } from '@/views/components/CopyContentButton/CopyContentButton'
import { FullTranscript } from '@/views/screens/Interview/components/FullTranscript/FullTranscript'
import { LiveTranscript } from '@/views/screens/Interview/components/LiveTranscript'
import { VideoPlayer } from '@/views/screens/Interview/components/VideoPlayer/VideoPlayer'
import { VideoPlayerProvider } from '@/views/screens/Interview/components/VideoPlayer/VideoPlayerContext'
import { useAudioRecorder } from '@/views/screens/Interview/context/AudioRecorderContext/AudioRecorderContext'

// Helper function to remove timestamps from transcript text
const cleanTranscriptFromTimestamps = (text: string): string => {
  // Remove timestamps like [00:27], [01:23], [01:23:45] and any trailing spaces
  return text.replace(/\[\d{1,2}:\d{2}(?::\d{2})?\]\s*/g, '')
}

export const TranscriptDisplay = () => {
  const { isRecording } = useAudioRecorder()

  const { data: interview, isLoading: isInterviewLoading } = useInterview()
  const updateInterview = useUpdateInterview()
  const isViewOnly = useIsViewOnlyMember()

  const [localFullTranscript, setLocalFullTranscript] = useState<
    string | undefined | null
  >(() => interview?.fullTranscript)
  const [isEditing, setIsEditing] = useState(false)
  const [seekToFn, setSeekToFn] = useState<
    ((timeInSeconds: number) => void) | null
  >(null)

  useEffect(() => {
    setLocalFullTranscript(interview?.fullTranscript)
  }, [interview?.fullTranscript])

  const handlePlayerReady = useCallback(
    (seekTo: (timeInSeconds: number) => void) => {
      setSeekToFn(() => seekTo)
    },
    [],
  )

  const isInterviewOptedOut = interview?.recallStatus === 'opted_out'
  const isInterviewAwaitingRecording =
    interview?.recallStatus === 'awaiting' ||
    interview?.recallStatus === 'processing' ||
    interview?.recallStatus === 'recording'

  const recordingVideoUrl = interview?.recordingVideoUrl ?? null

  const shouldShowVideo = !!recordingVideoUrl
  const shouldRenderVideoPlayer = isInterviewLoading || shouldShowVideo
  const isVideoLoading = isInterviewLoading && !recordingVideoUrl

  return (
    <VideoPlayerProvider value={seekToFn ? { seekTo: seekToFn } : null}>
      {localFullTranscript && (
        <Toolbox
          transcript={localFullTranscript}
          shouldShowVideo={shouldShowVideo}
          recordingVideoUrl={recordingVideoUrl}
        />
      )}
      {shouldRenderVideoPlayer && (
        <VideoPlayer
          onPlayerReady={handlePlayerReady}
          videoUrl={recordingVideoUrl}
          isLoading={isVideoLoading}
        />
      )}
      <Card className="flex flex-col px-[2.5rem] pt-[1.25rem] pb-[1.75rem] flex-grow-[2]">
        {isInterviewAwaitingRecording ? (
          <RecallNotice isRecording={interview?.recallStatus === 'recording'} />
        ) : isInterviewOptedOut ? (
          <OptedOutNotice optedOutByEmail={interview?.optedOutByEmail} />
        ) : interview?.transcriptionError ? (
          <TranscriptionErrorNotice error={interview.transcriptionError} />
        ) : (
          <>
            {isRecording ? (
              <MicrophoneRecordingNotice />
            ) : (
              <Box className="relative grow">
                <Box className="flex justify-between items-center mb-[0.75rem]">
                  <Box className="flex flex-row gap-x-2 items-center flex-wrap">
                    <Text fontWeight="bold" fontSize="1.25rem">
                      Raw transcript
                    </Text>
                    {interview?.FullTranscriptEditedBy && (
                      <Tooltip
                        rounded={'2.5rem'}
                        py={2}
                        px={3}
                        bgColor={'blackAlpha.900'}
                        placement="top"
                        label={
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                              size="sm"
                              src={interview.FullTranscriptEditedBy.photoURL}
                            />
                            <Text color="white" fontSize="sm">
                              {interview.FullTranscriptEditedBy.displayName}
                            </Text>
                          </Box>
                        }
                      >
                        <Text
                          fontSize="0.875rem"
                          color="#999999"
                          fontWeight="bold"
                          mt="0.25rem"
                          cursor="pointer"
                        >
                          edited
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                  {!isViewOnly && (
                    <Box className="flex gap-[0.6rem]">
                      <IconButton
                        onClick={() => {
                          if (isEditing) {
                            updateInterview.mutate({
                              fullTranscript: localFullTranscript,
                            })
                          }
                          setIsEditing((isEditing) => !isEditing)
                        }}
                        variant="brand-solid-gray"
                        icon={isEditing ? <CheckIcon /> : <PencilSvg />}
                        aria-label="Edit full transcript"
                      />
                    </Box>
                  )}
                </Box>
                <FullTranscript
                  transcript={localFullTranscript || ''}
                  setLocalTranscript={setLocalFullTranscript}
                  isEditing={isEditing}
                  localTranscript={localFullTranscript || ''}
                />
              </Box>
            )}
          </>
        )}
      </Card>
    </VideoPlayerProvider>
  )
}

const OptedOutNotice = (props: {
  optedOutByEmail: string | null | undefined
}) => (
  <Box maxW={600}>
    This interview has been opted out from recording
    {props.optedOutByEmail ? (
      <>
        {' '}
        by <b>{props.optedOutByEmail}</b>.
      </>
    ) : (
      '.'
    )}
  </Box>
)

const transcriptionErrorMessages: Record<TranscriptionError, string> = {
  SILENT_AUDIO:
    'The recording contained no speech. This can happen if the bot joined a meeting before participants arrived, or the audio was muted. You can upload a recording manually.',
}

const TranscriptionErrorNotice = ({ error }: { error: TranscriptionError }) => (
  <Box maxW={600}>{transcriptionErrorMessages[error]}</Box>
)

const RecallNotice = ({ isRecording }: { isRecording: boolean }) => (
  <Box maxW={600}>
    {isRecording && (
      <>
        Recording in progress...
        <br />
        <br />
      </>
    )}
    This interview will be transcribed automatically by our{' '}
    <b>{NOTETAKER_EMAIL}</b> bot. Please check in here 5 minutes after the
    interview is complete.
    <br />
    <br />
    In the meantime, feel free to add the candidate’s CV and role description.
  </Box>
)

const MicrophoneRecordingNotice = () => {
  const {
    sourceType,
    liveTranscriptSegments,
    liveTranscriptInterimSegment,
    isLiveTranscriptionConnected,
    liveTranscriptionError,
  } = useAudioRecorder()

  const hasLiveTranscription =
    isLiveTranscriptionConnected ||
    liveTranscriptSegments.length > 0 ||
    liveTranscriptionError

  if (hasLiveTranscription) {
    return (
      <Box className="flex flex-col h-full">
        <LiveTranscript
          segments={liveTranscriptSegments}
          interimSegment={liveTranscriptInterimSegment}
          isConnected={isLiveTranscriptionConnected}
          error={liveTranscriptionError}
        />
        {sourceType === 'micRecording' && (
          <Text color="gray.500" fontSize="sm" mt={3}>
            Make sure to use speakers, not headphones, so that hirehire can hear
            all participants in the call through your mic.
          </Text>
        )}
      </Box>
    )
  }

  return (
    <Text maxW={600}>
      This interview will be transcribed automatically once the recording is
      complete.
      <br />
      <br />
      {sourceType === 'micRecording' && (
        <>
          Make sure to use speakers, not headphones, so that hirehire can hear
          all participants in the call through your mic.
          <br />
          <br />
        </>
      )}
      Please, always ask for the candidate&apos;s consent before recording.
    </Text>
  )
}

export const Toolbox = ({
  transcript,
  shouldShowVideo,
  recordingVideoUrl,
}: {
  transcript: string
  shouldShowVideo: boolean
  recordingVideoUrl: string | null
}) => {
  const noTimestampsTranscript = transcript
    ? cleanTranscriptFromTimestamps(transcript)
    : ''

  const handleDownloadVideo = () => {
    if (!recordingVideoUrl) {
      return
    }

    const link = document.createElement('a')
    link.href = recordingVideoUrl
    link.download = 'interview-recording.mp4'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Card className="mb-5 flex flex-row items-center p-[1.25rem] flex-wrap rounded-[2.5rem] bg-[#F5F5F5] gap-x-3 gap-y-3">
      {shouldShowVideo && recordingVideoUrl && (
        <Button
          leftIcon={<DownloadIcon />}
          onClick={handleDownloadVideo}
          variant="brand-solid-white"
          aria-label="Download video"
        >
          Download Video
        </Button>
      )}
      <Box marginLeft="auto">
        <CopyContentButton
          textContent={{
            'text/plain': noTimestampsTranscript,
          }}
          textCopy="Copy"
          isDisabled={!transcript}
          aria-label="Copy transcript"
          variant="brand-solid"
        />
      </Box>
    </Card>
  )
}
